import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { BookingSearchData } from '../../pages/Booking';
import type { Vehicle } from '../../types/booking';
import { apiGetDayTrips } from '../../api/dayTrips/dayTripApi';
import BookingItineraryCard, {
  type BookingStop,
} from './BookingItineraryCard';

type PopularStop = {
  id: string;
  title: string;
  category: string;
  image: string;
};

type BookStep2Props = {
  selectedVehicle: Vehicle | null;
  onBack: () => void;
  onNext: () => void;
  searchData: BookingSearchData;
  stops: BookingStop[];
  onStopsChange: Dispatch<SetStateAction<BookingStop[]>>;
  onEditRoute?: () => void;
  onSearchDataChange?: (updater: (prev: BookingSearchData) => BookingSearchData) => void;
  servicePrice?: number;
  serviceTitle?: string;
  serviceType?: string;
};

export default function BookStep2({
  selectedVehicle,
  onBack,
  onNext,
  searchData,
  stops,
  onStopsChange,
  onEditRoute,
  onSearchDataChange,
  servicePrice,
  serviceTitle,
  serviceType,
}: BookStep2Props) {
  const [popularStops, setPopularStops] = useState<PopularStop[]>([]);

  useEffect(() => {
    let mounted = true;
    apiGetDayTrips().then((res) => {
      if (mounted && res?.data && res.data.length > 0) {
        setPopularStops(
          res.data.slice(0, 4).map((trip) => ({
            id: trip.id,
            title: trip.endLocation ? `${trip.title}, ${trip.endLocation}` : trip.title,
            category: trip.highlights?.[0] || "Sightseeing",
            image: trip.images?.[0] || trip.image || "/assets/images/day-trip-hallstatt.png",
          }))
        );
      }
    }).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const addPopularStop = (popularStop: PopularStop) => {
    onStopsChange((currentStops) => {
      if (currentStops.length === 0) {
        return [
          {
            id: Date.now(),
            location: popularStop.title,
            type: popularStop.category,
            duration: "45 min",
          },
        ];
      }

      return currentStops.map((stop, index) =>
        index === 0
          ? {
            ...stop,
            location: popularStop.title,
            type: popularStop.category,
            duration: "45 min",
          }
          : stop,
      );
    });
  };

  return (
    <section className="relative px-4 pb-[150px] sm:px-8 lg:px-[6vw]">
      <div className="mx-auto w-full max-w-[1450px]">
        <div className="mt-8">
          <h1 className="font-host text-[22px] font-semibold tracking-[-0.03em] text-black sm:text-[30px]">
            Add Stops to Your Journey
          </h1>

          <p className="mt-2 font-poppins text-[14px] leading-[1.55] text-[#333333] sm:text-[16px]">
            Enhance your trip by adding stops for sightseeing, meals, shopping
            or any other needs.
          </p>
        </div>

        <div className="mt-5 grid items-start gap-8 lg:grid-cols-[minmax(0,1.34fr)_minmax(410px,0.86fr)] lg:gap-10">
          <div>
            {popularStops.length > 0 && (
              <div>
                <h2 className="font-host text-[23px] font-semibold text-black">
                  Popular Stop Suggestions
                </h2>

                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                {popularStops.map((popularStop) => (
                  <article
                    key={popularStop.id}
                    className="grid h-[150px] min-h-[150px] grid-cols-[44%_minmax(0,1fr)] overflow-hidden rounded-[16px] bg-[#EEEEEE] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  >
                    <div className="h-[150px] w-full overflow-hidden bg-[#E4E4E4]">
                      <img
                        src={popularStop.image}
                        alt={popularStop.title}
                        className="h-full w-full object-cover object-center"
                      />
                    </div>

                    <div className="flex min-w-0 flex-col justify-center px-3 py-3 sm:px-5 sm:py-4">
                      <h3 className="line-clamp-2 font-host text-[14px] font-semibold leading-tight text-black sm:line-clamp-1 sm:text-[17px]">
                        {popularStop.title}
                      </h3>

                      <p className="mt-1.5 line-clamp-1 font-host text-[13px] text-black sm:mt-2 sm:text-[15px]">
                        {popularStop.category}
                      </p>

                      <button
                        type="button"
                        onClick={() => addPopularStop(popularStop)}
                        className="mt-3 w-fit rounded-[7px] bg-[#B8D877] px-4 py-2 font-host text-[12px] font-medium text-black transition-transform duration-200 hover:-translate-y-0.5 active:scale-95 sm:mt-4 sm:px-5 sm:py-2.5 sm:text-[13px]"
                      >
                        + Add Stop
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          </div>

          <BookingItineraryCard
            searchData={searchData}
            selectedVehicle={selectedVehicle}
            stops={stops}
            onEditRoute={onEditRoute}
            onSearchDataChange={onSearchDataChange}
            servicePrice={servicePrice}
            serviceTitle={serviceTitle}
            serviceType={serviceType}
          />
        </div>
      </div>

      <div className="fixed bottom-3 left-1/2 z-50 w-[calc(100%-1.5rem)] -translate-x-1/2 rounded-[18px] border border-[#ECECEC] bg-white/95 px-3 py-3 shadow-[0_5px_22px_rgba(0,0,0,0.14)] backdrop-blur-md sm:bottom-4 sm:w-[88vw] sm:rounded-[20px] sm:px-10 sm:py-5 lg:w-[68vw] lg:max-w-[1120px]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <button
            type="button"
            onClick={onBack}
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-black px-4 font-poppins text-[12px] font-medium text-white sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
          >
            Back
          </button>

          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto sm:gap-4">
            <button
              type="button"
              onClick={onNext}
              className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-[#FFE8DF] px-4 font-poppins text-[12px] font-medium text-black transition-transform duration-200 hover:-translate-y-0.5 sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
            >
              Skip Stops
            </button>

            <button
              type="button"
              onClick={onNext}
              className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-[#FF6B2C] px-4 font-poppins text-[12px] font-medium text-white transition-transform duration-200 hover:-translate-y-0.5 sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
